/* =============================================================================
   Collection card behaviour. PeekAPaw customisation.

   Three independent pieces, each in its own IIFE so one failing cannot take
   the others down:

     1. <variant-card-picker>  the pill picker on each card
     2. add-to-cart toast      fires on any successful POST to /cart/add
     3. in-stock-only filter   injected above the product grid

   Pairs with assets/variant-card.css and the vc-* markup in
   snippets/product-item.liquid.
   ========================================================================== */

/* --- 1. variant picker ---------------------------------------------------- */

(function () {
  if (window.customElements && customElements.get('variant-card-picker')) {
    return;
  }

  class VariantCardPicker extends HTMLElement {
    connectedCallback() {
      if (!this._initialised) {
        this._initialised = true;
        this.addEventListener('change', this._onChange.bind(this));
      }

      // The browser restores radio state on back/forward navigation, which can
      // leave the checked pill out of sync with the server-rendered card.
      const checked = this._checkedInput();
      const idInput = this._idInput();

      if (checked && idInput && idInput.value !== checked.value) {
        this._select(checked);
      }
    }

    _form() {
      return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    }

    _idInput() {
      const form = this._form();
      return form ? form.querySelector('input[name="id"]') : null;
    }

    _checkedInput() {
      return this.querySelector('input[type="radio"]:checked');
    }

    _onChange(event) {
      const input = event.target;

      if (input && input.type === 'radio') {
        this._select(input);
      }
    }

    _select(input) {
      const data = input.dataset;
      const card = this.closest('.product-item');
      const form = this._form();

      // 1. the variant that actually gets added to the cart
      const idInput = this._idInput();
      if (idInput) {
        idInput.value = input.value;
      }

      // 2. the corner ribbon
      const flag = card && card.querySelector('.vc-flag');
      if (flag) {
        flag.setAttribute('data-status', data.status || 'in-stock');

        const flagText = flag.querySelector('.vc-flag__text') || flag.querySelector('.vc-flag__label');
        if (flagText) {
          flagText.textContent = data.statusLabel || '';
        }

        // The ribbon is a real link to the pre-order explainer. CSS gates
        // clicks with pointer-events; keep keyboard access in step with it.
        const flagLink = flag.querySelector('[data-vc-flag-link]');
        if (flagLink) {
          if (data.status === 'pre-order') {
            flagLink.removeAttribute('tabindex');
          } else {
            flagLink.setAttribute('tabindex', '-1');
          }
        }
      }

      // 3. the price
      const priceHost = card && card.querySelector('[data-vc-price]');
      if (priceHost) {
        const strings = window.languages || {};
        const sr = function (text) {
          return '<span class="visually-hidden">' + (text || '') + '</span>';
        };

        priceHost.innerHTML = data.compare
          ? '<span class="price price--highlight">' + sr(strings.productSalePrice) + data.price + '</span>' +
            '<span class="price price--compare">' + sr(strings.productRegularPrice) + data.compare + '</span>'
          : '<span class="price">' + sr(strings.productSalePrice) + data.price + '</span>';
      }

      // 4. unit price and the BBD / ETA date beside it
      const unitHost = card && card.querySelector('[data-vc-unit]');
      if (unitHost) {
        unitHost.textContent = data.unit || '';
        this._toggle(unitHost, !!data.unit);
      }

      const dateHost = card && card.querySelector('[data-vc-date]');
      if (dateHost) {
        dateHost.textContent = data.date || '';
        dateHost.setAttribute('data-kind', data.dateKind || '');
        this._toggle(dateHost, !!data.date);
      }

      const infoWrap = card && card.querySelector('[data-vc-info-wrap]');
      if (infoWrap) {
        this._toggle(infoWrap, !!(data.unit || data.date));
      }

      // 4b. the discount badge on the price line
      const saveWrap = card && card.querySelector('[data-vc-save]');
      if (saveWrap) {
        saveWrap.textContent = data.savePct || '';
        this._toggle(saveWrap, !!data.savePct);
      }

      // 4c. the wrapper around the discount chip. It carries the row gap, so
      //     it has to disappear when the chip is not showing.
      const meta = card && card.querySelector('[data-vc-meta]');
      if (meta) {
        this._toggle(meta, !!data.savePct);
      }

      // 5. the add to cart button. Pre-order keeps the "Add to cart" wording
      //    and only changes colour.
      const button = form && form.querySelector('[data-vc-atc]');
      if (button) {
        const soldOut = data.status === 'sold-out';
        const base = soldOut
          ? (this.dataset.classDisabled || '')
          : (this.dataset.classEnabled || '');

        button.disabled = soldOut;
        button.className = base;
        button.textContent = soldOut
          ? (this.dataset.labelSoldOut || '')
          : (this.dataset.labelAddToCart || '');
      }

      // 6. point the card's links at the chosen variant
      if (card && data.url) {
        card.querySelectorAll('[data-vc-url]').forEach(function (link) {
          link.setAttribute('href', data.url);
        });
      }
    }

    _toggle(el, show) {
      if (show) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', 'hidden');
      }
    }
  }

  customElements.define('variant-card-picker', VariantCardPicker);
})();

/* --- 2. add to cart toast ------------------------------------------------- */

(function () {
  if (window.__vcToastInstalled) return;
  window.__vcToastInstalled = true;

  const VISIBLE_MS = 2600;
  let region = null;
  let lastShownAt = 0;

  function getRegion() {
    if (region && region.isConnected) return region;
    region = document.createElement('div');
    region.className = 'vc-toast-region';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
    return region;
  }

  function labelFor(item) {
    const fallback = (window.languages && window.languages.productAdded) || 'Added to cart';
    if (!item || !item.product_title) return fallback;

    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? ' — ' + item.variant_title
      : '';

    return fallback + ': ' + item.product_title + variant;
  }

  function show(item) {
    // Some themes fire more than one request per click; collapse those.
    const now = (window.performance && performance.now) ? performance.now() : new Date().getTime();
    if (now - lastShownAt < 400) return;
    lastShownAt = now;

    const toast = document.createElement('div');
    toast.className = 'vc-toast';
    toast.innerHTML =
      '<span class="vc-toast__tick" aria-hidden="true">✓</span>' +
      '<span class="vc-toast__text"></span>';
    toast.querySelector('.vc-toast__text').textContent = labelFor(item);

    getRegion().appendChild(toast);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      toast.classList.add('is-leaving');
      setTimeout(function () { toast.remove(); }, 400);
    }, VISIBLE_MS);
  }

  function isCartAdd(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url.split('#')[0]);
  }

  // fetch
  if (window.fetch) {
    const nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      const promise = nativeFetch.apply(this, arguments);

      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (isCartAdd(url)) {
          promise.then(function (response) {
            if (!response || !response.ok) return;
            response.clone().json().then(show).catch(function () { show(null); });
          }).catch(function () {});
        }
      } catch (e) { /* never break the request */ }

      return promise;
    };
  }

  // XMLHttpRequest, in case an app or an older code path uses it
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__vcCartAdd = isCartAdd(url);
    if (this.__vcCartAdd) {
      this.addEventListener('load', function () {
        if (this.status < 200 || this.status >= 300) return;
        let item = null;
        try { item = JSON.parse(this.responseText); } catch (e) { /* ignore */ }
        show(item);
      });
    }
    return nativeOpen.apply(this, arguments);
  };
})();

/* --- 3. stock filters ------------------------------------------------------ */
/* Two mutually exclusive checkboxes; either one, or neither. */

(function () {
  if (window.__vcStockFilterInstalled) return;
  window.__vcStockFilterInstalled = true;

  const KEY = 'vc-stock-filter-mode';

  const MODES = [
    {
      id: 'in-stock',
      cls: 'vc-in-stock-only',
      label: 'In-stock only',
      keep: ['in-stock']
    },
    {
      id: 'hide-sold-out',
      cls: 'vc-hide-sold-out',
      label: 'Hide sold out',
      keep: ['in-stock', 'pre-order']
    }
  ];

  function stored() {
    try { return sessionStorage.getItem(KEY) || ''; } catch (e) { return ''; }
  }

  function persist(id) {
    try {
      id ? sessionStorage.setItem(KEY, id) : sessionStorage.removeItem(KEY);
    } catch (e) { /* private mode */ }
  }

  function current() {
    const id = stored();
    for (let i = 0; i < MODES.length; i++) {
      if (MODES[i].id === id) return MODES[i];
    }
    return null;
  }

  function apply() {
    const active = current();
    MODES.forEach(function (mode) {
      document.documentElement.classList.toggle(mode.cls, !!active && active.id === mode.id);
    });
  }

  function isListingPage() {
    const cls = document.body ? document.body.className : '';
    return /template-collection|template-search/.test(cls);
  }

  function grid() {
    const first = document.querySelector('#main .product-item[data-vc-has-in-stock]');
    return first ? first.parentElement : null;
  }

  // When the active filter hides the pill that happens to be selected, move the
  // card onto a pill the filter keeps, so the buy button stays truthful.
  function resync() {
    const active = current();
    if (!active) return;

    document.querySelectorAll('variant-card-picker').forEach(function (picker) {
      const checked = picker.querySelector('input[type="radio"]:checked');
      if (checked && active.keep.indexOf(checked.dataset.status) !== -1) return;

      let target = null;
      for (let i = 0; i < active.keep.length && !target; i++) {
        target = picker.querySelector('input[type="radio"][data-status="' + active.keep[i] + '"]');
      }
      if (!target) return;

      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function build() {
    const wrap = document.createElement('div');
    wrap.className = 'vc-stock-filter';

    const boxes = [];

    MODES.forEach(function (mode) {
      const label = document.createElement('label');
      label.className = 'vc-stock-filter__label';

      const box = document.createElement('input');
      box.type = 'checkbox';
      box.className = 'vc-stock-filter__box';
      box.value = mode.id;
      box.checked = stored() === mode.id;

      const text = document.createElement('span');
      text.textContent = mode.label;

      label.appendChild(box);
      label.appendChild(text);
      wrap.appendChild(label);
      boxes.push(box);

      box.addEventListener('change', function () {
        if (box.checked) {
          boxes.forEach(function (other) { if (other !== box) other.checked = false; });
        }
        persist(box.checked ? mode.id : '');
        apply();
        resync();
      });
    });

    return wrap;
  }

  function ensure() {
    if (!isListingPage()) return;

    const existing = document.querySelector('.vc-stock-filter');
    if (existing && existing.isConnected) {
      existing.querySelectorAll('.vc-stock-filter__box').forEach(function (box) {
        box.checked = stored() === box.value;
      });
      return;
    }

    const container = grid();
    if (!container || !container.parentNode) return;

    container.parentNode.insertBefore(build(), container);
    resync();
  }

  apply();

  function start() {
    ensure();

    // The collection section is re-rendered wholesale by filters, sorting and
    // pagination, which throws the toggle away. Put it back when that happens.
    let queued = null;
    const observer = new MutationObserver(function () {
      if (queued) return;
      queued = setTimeout(function () { queued = null; ensure(); }, 120);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
